import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CheckCircle } from "@phosphor-icons/react";
import { apiGet, apiSend } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import { submitMonriForm, type MonriFormParams } from "@/lib/monri";
import { formatDateBs, formatTimeBs } from "@/lib/timezone";
import { formatCurrency } from "@/lib/format";

export const Route = createFileRoute("/booking/finalize/$token")({
  component: FinalizePage,
});

interface FinalizeMeta { sessionId: string; serviceName: string; therapistName: string; scheduledAt: string; amount: number; holdExpiresAt: string | null; }
interface MonriResp { monri: MonriFormParams; }

// The API returns "Rezervacija je već plaćena." with a 409 when the hold has
// already been settled — a good outcome for the client, not an error.
const ALREADY_PAID_MSG = "Rezervacija je već plaćena.";

function FinalizePage() {
  const { token } = Route.useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["finalize", token],
    queryFn: () => apiGet<FinalizeMeta>(`/api/booking/finalize/${token}`, "Link nije važeći."),
    retry: false,
  });
  const payMutation = useMutation({
    mutationFn: () => apiSend<MonriResp>(`/api/booking/finalize/${token}/pay`, "POST", {}, "Greška pri plaćanju."),
    onSuccess: (resp) => {
      submitMonriForm(resp.monri);
    },
    // Greška se prikazuje ispod dugmeta; prazan onError gasi globalni toast da ne bude dva puta.
    onError: () => {},
  });
  if (isLoading) return <div className="mx-auto max-w-md p-8 text-center">Učitavanje…</div>;
  if (error || !data) {
    const message = error instanceof Error && error.message ? error.message : "Link nije važeći.";
    // The "already paid" case is a positive confirmation, not a failure.
    if (message === ALREADY_PAID_MSG) {
      return (
        <div className="mx-auto max-w-md space-y-4 p-8 text-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-full" style={{ background: "oklch(92% 0.05 155)" }}>
            <CheckCircle className="size-7" style={{ color: "oklch(55% 0.15 155)" }} />
          </div>
          <h1 className="text-xl font-bold">Rezervacija je već plaćena</h1>
          <p className="text-sm text-muted-foreground">Vaš termin je potvrđen. Detalje možete vidjeti u "Moji termini".</p>
          <Link to="/dashboard/client/sessions">
            <Button className="w-full">Moji termini</Button>
          </Link>
        </div>
      );
    }
    return <div className="mx-auto max-w-md p-8 text-center text-red-600">{message}</div>;
  }
  return (
    <div className="mx-auto max-w-md space-y-4 p-8">
      <h1 className="text-xl font-bold">Finalizirajte rezervaciju</h1>
      <div className="rounded-lg border p-4 space-y-1 text-sm">
        <div><strong>Usluga:</strong> {data.serviceName}</div>
        <div><strong>Terapeut:</strong> {data.therapistName}</div>
        <div><strong>Termin:</strong> {formatDateBs(data.scheduledAt)} u {formatTimeBs(data.scheduledAt)}</div>
        <div><strong>Iznos:</strong> {formatCurrency(data.amount)}</div>
        {data.holdExpiresAt && (<div className="text-amber-700"><strong>Rezervacija ističe:</strong> {formatDateBs(data.holdExpiresAt)} u {formatTimeBs(data.holdExpiresAt)}</div>)}
      </div>
      <Button onClick={() => payMutation.mutate()} disabled={payMutation.isPending} className="w-full">
        {payMutation.isPending ? "Preusmjeravanje…" : "Plati karticom"}
      </Button>
      {payMutation.isError && (
        <p className="text-center text-sm text-red-600">
          {payMutation.error instanceof Error && payMutation.error.message ? payMutation.error.message : "Greška pri plaćanju."}
        </p>
      )}
    </div>
  );
}
